import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import LockIcon from "@mui/icons-material/Lock";

export function PromptDialog({
                                 open,
                                 title,
                                 label,
                                 defaultValue = '',
                                 onConfirm,
                                 onCancel,
                                 isSecret,
                                 confirmText = 'Submit',
                                 cancelText = 'Cancel',
                                 disableMultiline
                             }) {
    const [inputValue, setInputValue] = useState(defaultValue);
    const [expandedProtection, setExpandedProtection] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (open) {
            setInputValue(defaultValue);
        }
    }, [open, defaultValue]);

    const handleConfirm = useCallback(() => {
        onConfirm(inputValue);
        setInputValue('');
    }, [onConfirm, inputValue]);

    const handleCancel = useCallback(() => {
        onCancel();
        setInputValue('');
    }, [onCancel]);

    const handleKeyDown = useCallback(
        (event) => {
            if (
                event.key === 'Enter' &&
                !event.shiftKey &&
                !event.ctrlKey &&
                !event.metaKey
            ) {
                event.preventDefault();
                if (inputValue.trim()) {
                    handleConfirm();
                }
            }
        },
        [handleConfirm, inputValue]
    );

    const handleExpandProtectionClick = () => {
        setExpandedProtection(!expandedProtection);
    };

    return (
        <Dialog
            style={{ zIndex: 10000 }}
            open={open}
            // onClose={handleCancel}
            aria-labelledby="prompt-dialog-title"
            aria-describedby="prompt-dialog-description"
            fullWidth
            maxWidth="xs"
            PaperProps={{
                sx: {
                    width: isMobile ? '90%' : 'auto',
                    minWidth: isMobile ? '90%' : '500px',
                },
            }}
        >
            <DialogTitle id="prompt-dialog-title">{title}</DialogTitle>
            <DialogContent>
                {isSecret ? (
                    <Box pt={3} pb={2}>
                        <Box p={3}  >
                            <TextField
                                label={
                                    <Box display="flex" alignItems="center">
                                        <LockIcon color="primary" />
                                        <Box ml={1}>{label}</Box>
                                    </Box>
                                }
                                autoFocus
                                margin="dense"
                                type="password"
                                fullWidth
                                variant="outlined"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <Box pt={2} pb={2}>
                                <Accordion expanded={expandedProtection} onChange={handleExpandProtectionClick}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="protection-content"
                                        id="protection-header"
                                    >
                                        <Typography variant="body2" color="textSecondary">
                                            How we protect your secrets?
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography variant="body2" color="textSecondary" align="left">
                                            <ReactMarkdown>
                                                {`Your secrets are double-encrypted—first with your private AES key (before leaving the browser) and then with a public asymmetric key (before being stored)—ensuring that the storage service cannot decrypt them. Only the code execution service, upon your AES key authorization, can decrypt and execute them.`}
                                            </ReactMarkdown>
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <>
                        <TextField
                            multiline={!disableMultiline}
                            autoFocus
                            margin="dense"
                            label={label}
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        {!disableMultiline && (
                            <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
                                Press <strong>Enter</strong> to submit. Use <strong>Shift + Enter</strong> to add a new line.
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} style={{ color: 'grey' }}>
                    {cancelText}
                </Button>
                <Button
                    autoFocus
                    onClick={handleConfirm}
                    color="primary"
                    variant="contained"
                    disabled={!inputValue.trim()}
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
}